import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Check, Loader2, Printer, Search, Send, Truck, XCircle,
} from 'lucide-react';

import { loadCarrierOptions, loadCarrierQuotes, printShippingLabel, purchaseCarrierLabel } from './api';
import { XomicsSelect } from './parts';
import { tx } from './i18n';
import { ShippingPrinterSelect, printerName, useShippingPrinters } from './ShippingLabelButton';
import { Field, errorMessage, fieldAria } from './ui';

// Whether a new label is printed as soon as it is made, as chosen last on this
// computer. It only matters where a shipping printer is connected.
const PRINT_KEY = 'xomics-shipping-print';
function printsNewLabels() { try { return window.localStorage.getItem(PRINT_KEY) !== 'off'; } catch (error) { return true; } }
function rememberPrinting(value) { try { window.localStorage.setItem(PRINT_KEY, value ? 'on' : 'off'); } catch (error) { /* kept for this visit only */ } }

function money(minor, currency) {
  if (currency === null || currency === undefined) return '—';
  return `${(Number(minor) / 100).toFixed(2)} ${currency}`;
}

function boxSize(box) {
  return `${box.lengthCm} × ${box.widthCm} × ${box.heightCm}`;
}

// Shipping an order or a freeform shipment: the box (suggested from the items,
// changeable), the return labels, which carriers can take it and why not, then
// quotes from the ones that can, and the label. `subject` is what is shipped:
// { subjectType: 'order', subjectUuid } or { subjectType: 'freeform', draft }.
export default function ShippingComposer({ subject, onDone }) {
  // Callers pass a fresh object each render; the panel follows its content.
  const subjectKey = JSON.stringify(subject);
  const shipped = useMemo(() => JSON.parse(subjectKey), [subjectKey]);
  const key = shipped.subjectUuid ?? 'freeform';
  const [options, setOptions] = useState(null);
  const [boxCode, setBoxCode] = useState('');
  const [returnLabels, setReturnLabels] = useState('');
  const [quotes, setQuotes] = useState(null);
  const [quoteId, setQuoteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shipping = useShippingPrinters();
  const [printLabels, setPrintLabels] = useState(printsNewLabels());
  const [printing, setPrinting] = useState(false);
  // A purchase whose labels did not come out of the printer: the labels exist,
  // so the choice left is to print again or to carry on without.
  const [bought, setBought] = useState(null);
  const printedLabels = useRef(0);

  // Step one: the box (suggested from the items, changeable), the return labels
  // (one per kit suggested) and which carriers can take the shipment with them.
  // Changing either asks again and drops any quotes.
  const loadOptions = useCallback(async (choice = {}) => {
    setBusy(true);
    setError('');
    setQuotes(null);
    setQuoteId('');
    try {
      const result = await loadCarrierOptions({ ...shipped, ...choice });
      setOptions(result);
      setBoxCode(result.parcel.box.code);
      setReturnLabels(String(result.returnLabels));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }, [shipped]);

  React.useEffect(() => { loadOptions(); }, [loadOptions]);

  const count = returnLabels === '' ? null : Number(returnLabels);
  const choice = { boxCode, returnLabels: count };
  const eligible = options?.carriers.filter((carrier) => carrier.eligible) ?? [];

  function chooseBox(code) {
    setBoxCode(code);
    loadOptions({ boxCode: code, returnLabels: count });
  }

  function chooseReturns(value) {
    setReturnLabels(value);
    if (value !== '' && Number.isInteger(Number(value)) && Number(value) >= 0 && Number(value) <= 20) {
      loadOptions({ boxCode, returnLabels: Number(value) });
    }
  }

  // Step two, only once the box and return labels are chosen: quotes from the
  // carriers that can take the shipment.
  async function compare() {
    setBusy(true);
    setError('');
    try {
      const result = await loadCarrierQuotes({ ...shipped, ...choice });
      setQuotes(result);
      setQuoteId(result.quotes[0]?.quoteId ?? '');
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  // The outbound label and every return label, one after the other. A label
  // that came out is not printed again when the rest is retried. The caller
  // ends the printing state, once the host has taken the shipment.
  async function printAll(result) {
    setPrinting(true);
    for (let index = printedLabels.current; index < result.shipment.labels.length; index += 1) {
      await printShippingLabel(result.shipment.labels[index].labelUrl, shipping.printer.deviceId);
      printedLabels.current = index + 1;
    }
  }

  async function purchase() {
    setBusy(true);
    setError('');
    try {
      const result = await purchaseCarrierLabel({ ...shipped, quoteId, ...choice });
      printedLabels.current = 0;
      if (shipping.printer !== null && printLabels) {
        try {
          await printAll(result);
        } catch (printError) {
          setQuotes(null);
          setQuoteId('');
          setBought(result);
          setError(errorMessage(printError));
          return;
        }
      }
      // The chosen service and its busy button stay on screen until the host
      // shows the new shipment; clearing the form first would put the first
      // view back for a moment, saying the quotes are being checked.
      await onDone(result);
      setQuotes(null);
      setQuoteId('');
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
      setPrinting(false);
    }
  }

  async function printAgain() {
    setBusy(true);
    setError('');
    try {
      await printAll(bought);
      await onDone(bought);
    } catch (printError) {
      setError(errorMessage(printError));
    } finally {
      setBusy(false);
      setPrinting(false);
    }
  }

  if (bought !== null) {
    return (
      <div className="xo-order-carrier-quotes">
        <p className="xo-alert t-bad" role="alert"><strong>{tx('ui.devices.print.failed_after_purchase')}</strong> {error}</p>
        <ShippingPrinterSelect shipping={shipping} disabled={busy} />
        <footer>
          <button type="button" className="xo-btn-2" disabled={busy} onClick={() => onDone(bought)}>{tx('ui.devices.print.continue')}</button>
          <button type="button" className="xo-btn" disabled={busy} onClick={printAgain}>
            {busy ? <Loader2 className="xo-spin" aria-hidden="true" /> : <Printer aria-hidden="true" />}
            {busy ? tx('ui.devices.print.printing') : tx('ui.devices.print.again')}
          </button>
        </footer>
      </div>
    );
  }

  if (options === null) {
    return (
      <div className="xo-order-carrier-start">
        <div>
          <span className="xo-order-carrier-icon"><Truck aria-hidden="true" /></span>
          <span><strong>{tx('ui.carrier.prepare_shipment')}</strong><small>{tx('ui.carrier.compare_services')}</small></span>
        </div>
        {busy && <Loader2 className="xo-spin" aria-hidden="true" />}
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      </div>
    );
  }

  return (
    <div className="xo-order-carrier-quotes">
      <header>
        <div className="xo-order-shipping-title">
          <strong>{tx('ui.carrier.prepare_shipment')}</strong>
          <small>{tx('ui.carrier.compare_services')}</small>
        </div>
        <span>{options.parcel.weightGrams.toLocaleString()} g</span>
      </header>

      <div className="xo-order-shipping-fields">
        <Field id={`box-${key}`} label={tx('ui.carrier.step_box')}
          hint={options.suggestedBoxCode
            ? `${tx('ui.carrier.box_suggested')}: ${options.boxes.find((box) => box.code === options.suggestedBoxCode)?.name}`
            : undefined}>
          <XomicsSelect {...fieldAria(`box-${key}`, { hint: Boolean(options.suggestedBoxCode) })}
            value={boxCode} disabled={busy} onChange={(event) => chooseBox(event.target.value)}>
            {options.boxes.map((box) => (
              <option key={box.code} value={box.code}>{tx('ui.carrier.box_option', { box: box.name, size: boxSize(box) })}</option>
            ))}
          </XomicsSelect>
        </Field>
        <Field id={`return-labels-${key}`} label={tx('ui.carrier.return_labels_count')}
          hint={tx('ui.carrier.return_labels_suggested', { count: options.suggestedReturnLabels })}>
          <input {...fieldAria(`return-labels-${key}`, { hint: true })} type="number" min="0" max="20" step="1"
            value={returnLabels} disabled={busy} onChange={(event) => chooseReturns(event.target.value)} />
        </Field>
      </div>

      <div className="xo-order-provider-list">
        <strong>{tx('ui.carrier.step_carriers')}</strong>
        {options.carriers.map((carrier) => (
          <div key={carrier.carrierId} className={carrier.eligible ? '' : 'is-unavailable'}>
            <span>
              <strong>{carrier.carrierName}</strong>
              <small>{carrier.eligible ? tx('ui.carrier.can_take') : tx('ui.carrier.cannot_take')}</small>
              {carrier.reasons.map((reason) => <small key={reason} className="t-bad">{reason}</small>)}
            </span>
            <span className={carrier.eligible ? 'is-supported' : 'is-unsupported'}>
              {carrier.eligible ? <Check aria-hidden="true" /> : <XCircle aria-hidden="true" />}
            </span>
          </div>
        ))}
      </div>
      {eligible.length === 0 && <p className="xo-muted">{tx('ui.carrier.none_can_take')}</p>}

      {quotes === null ? (
        <footer>
          <button type="button" className="xo-btn" disabled={busy || eligible.length === 0 || count === null} onClick={compare}>
            {busy ? <Loader2 className="xo-spin" aria-hidden="true" /> : <Search aria-hidden="true" />}
            {busy ? tx('ui.carrier.checking_quotes') : tx('ui.carrier.get_quotes')}
          </button>
        </footer>
      ) : (
        <>
          <div className="xo-order-quote-list">
            {quotes.quotes.map((quote) => (
              <label key={quote.quoteId} className={quoteId === quote.quoteId ? 'is-selected' : ''}>
                <input type="radio" name={`order-carrier-quote-${key}`} value={quote.quoteId}
                  checked={quoteId === quote.quoteId} disabled={busy}
                  onChange={() => setQuoteId(quote.quoteId)} />
                <span>
                  <strong>{quote.carrierName}</strong>
                  <small>{quote.serviceName}{quote.estimatedDaysMin !== null ? ` · ${quote.estimatedDaysMin}–${quote.estimatedDaysMax} ${tx('ui.carrier.working_days')}` : ''}</small>
                  {quote.returnLabels > 0 && (
                    <small>{tx('ui.carrier.quote_breakdown', {
                      outbound: money(quote.outboundCostMinor, quote.currency),
                      count: quote.returnLabels,
                      returns: money(quote.returnCostMinor, quote.currency),
                    })}</small>
                  )}
                </span>
                <strong>{money(quote.totalCostMinor, quote.currency)}</strong>
              </label>
            ))}
          </div>
          {quotes.carriers.filter((carrier) => carrier.quoteError).map((carrier) => (
            <p key={carrier.carrierId} className="xo-alert t-bad">{carrier.carrierName}: {carrier.quoteError}</p>
          ))}
          {quotes.quotes.length === 0 && <p className="xo-muted">{tx('ui.carrier.no_services')}</p>}
          {shipping.printer !== null && (
            <div className="xo-order-print-choice">
              <label>
                <input type="checkbox" checked={printLabels} disabled={busy}
                  onChange={(event) => { setPrintLabels(event.target.checked); rememberPrinting(event.target.checked); }} />
                <Printer aria-hidden="true" />
                <span>{tx('ui.devices.print.labels_on', { printer: printerName(shipping.printer) })}</span>
              </label>
              <ShippingPrinterSelect shipping={shipping} disabled={busy || !printLabels} />
            </div>
          )}
          <footer>
            <button type="button" className="xo-btn-2" disabled={busy} onClick={() => setQuotes(null)}>{tx('ui.common.cancel')}</button>
            <button type="button" className="xo-btn" disabled={busy || quoteId === ''} onClick={purchase}>
              {busy ? <Loader2 className="xo-spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
              {printing ? tx('ui.devices.print.printing') : busy ? tx('ui.carrier.generating_label') : tx('ui.carrier.generate_label')}
            </button>
          </footer>
        </>
      )}
      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
    </div>
  );
}
